import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SelectControl, SelectItem } from 'src/app/form-controls/select/select.control';
import { Unit } from 'src/app/form-controls/shared/popovers/unit-popover/unit.popover';
import { UnitValue, UnitValueControl } from 'src/app/form-controls/unit-value/unit-value.control';
import { SheetItemService } from 'src/app/services/sheet-item.service';
import { ItemTypeMetadata, SheetItemFormBase } from 'src/app/services/sheets-renderer.service';
import { Row } from 'src/app/types/aggregate-graph.types';
import { MedicationAmountUnitV2, MedicationDoseUnitV2 } from 'src/app/types/sheet-action-aggregate-graph.types';
import { MedicationBlueLabelInstruction, MedicationRoute } from 'src/app/types/sheet-instruction.types';
import { InstructionsFormComponent } from '../../instructions-form/instructions-form.component';

interface MedicationBlueLabelInstructionFormGroup {
	dose: FormControl<UnitValue | null>;
	amountToAdminister: FormControl<UnitValue | null>;
	medicationRoute: FormControl<MedicationRoute | null>;
}

@Component({
    selector: 'app-medication-blue-label-item-form',
    templateUrl: './medication-blue-label-item-form.component.html',
    styleUrl: './medication-blue-label-item-form.component.scss',
    imports: [ReactiveFormsModule, InstructionsFormComponent, SelectControl, UnitValueControl],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicationBlueLabelItemFormComponent implements SheetItemFormBase, OnInit {

	@Input() instructionTypeName!: string;
	@Input() item!: Row | null;
	@Input() minDate!: string;
	@Input() maxDate!: string;
	@Input() instructionsFormArray!: FormArray;
  	@Input() itemConfig!: ItemTypeMetadata;

	medicationRoutes: SelectItem[] = [
		{ text: 'IV', value: MedicationRoute.Iv },
		{ text: 'IM', value: MedicationRoute.Im },
		{ text: 'SQ', value: MedicationRoute.Sq },
		{ text: 'Oral', value: MedicationRoute.Oral },
		{ text: 'Transmucosal', value: MedicationRoute.Transmucosal },
		{ text: 'Topical', value: MedicationRoute.Topical },
		{ text: 'Rectal', value: MedicationRoute.Rectal },
		{ text: 'Intranasal', value: MedicationRoute.Intranasal },
		{ text: 'Inhalation', value: MedicationRoute.Inhalation },
		{ text: 'Transdermal', value: MedicationRoute.Transdermal },
		{ text: 'OS', value: MedicationRoute.Os },
		{ text: 'OD', value: MedicationRoute.Od },
		{ text: 'OU', value: MedicationRoute.Ou }
	];

	doseUnits: Unit[] = [
		{ label: MedicationDoseUnitV2.Iu },
		{ label: MedicationDoseUnitV2.MgKg },
		{ label: MedicationDoseUnitV2.UgKg },
		{ label: MedicationDoseUnitV2.MgM2 },
		{ label: MedicationDoseUnitV2.IuM2 },
		{ label: MedicationDoseUnitV2.IuKg },
		{ label: MedicationDoseUnitV2.MgAnimal },
		{ label: MedicationDoseUnitV2.UgAnimal },
		{ label: MedicationDoseUnitV2.MlAnimal }
	];

	amountUnits: Unit[] = [
		{ label: MedicationAmountUnitV2.Mg },
		{ label: MedicationAmountUnitV2.Ml },
		{ label: MedicationAmountUnitV2.Ug },
		{ label: MedicationAmountUnitV2.L },
		{ label: MedicationAmountUnitV2.G },
		{ label: MedicationAmountUnitV2.Kg },
		{ label: MedicationAmountUnitV2.Drop },
		{ label: MedicationAmountUnitV2.Graduation },
		{ label: MedicationAmountUnitV2.Tablet },
		{ label: MedicationAmountUnitV2.Capsule },
		{ label: MedicationAmountUnitV2.Spray },
		{ label: MedicationAmountUnitV2.Pump },
		{ label: MedicationAmountUnitV2.Iu },
		{ label: MedicationAmountUnitV2.Na }
	];



	constructor(private sheetItemService: SheetItemService, private formBuilder: FormBuilder) {
	}

	ngOnInit() {
		this.sheetItemService.getInstructions(this.item, this.instructionTypeName, this.itemConfig.scheduleConfig.defaultScheduleType, this.itemConfig.scheduleConfig.defaultRepeatingScheduleFrequency).forEach(instruction => {
			const group: FormGroup<MedicationBlueLabelInstructionFormGroup> = this.sheetItemService.buildExistingInstructionGroup(this.instructionTypeName, instruction);
			const medicationInstruction = instruction as MedicationBlueLabelInstruction;
			group.addControl('dose', this.formBuilder.control(medicationInstruction.dose ?? null));
			group.addControl('amountToAdminister', this.formBuilder.control(medicationInstruction.amountToAdminister ?? null));
			group.addControl('medicationRoute', this.formBuilder.control(medicationInstruction.medicationRoute ?? null));
			this.instructionsFormArray.push(group);
		});
	}

	addInstruction() {
		const group: FormGroup<MedicationBlueLabelInstructionFormGroup> = this.sheetItemService.getNewInstructionGroup(this.instructionTypeName, this.itemConfig.scheduleConfig.defaultScheduleType, this.itemConfig.scheduleConfig.defaultRepeatingScheduleFrequency);
		group.addControl('dose', this.formBuilder.control(null));
		group.addControl('amountToAdminister', this.formBuilder.control(null));
		group.addControl('medicationRoute', this.formBuilder.control(null));
		this.instructionsFormArray.push(group);
	}
}